import { useCallback, useState } from "react";
import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from "@mantine/core";
import { FiSlash, FiRotateCcw } from "react-icons/fi";
import type { RoomValidationDiffItem } from "@multiconsult-group/multiplacer-core";
import { openapi } from "../../openapi";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { selectProjectNo } from "../sharedSelectors";
import { ViewerValidationDiffTable } from "./ViewerValidationDiffTable";
import {
  selectArticleExclusionSettings,
  selectArticleExclusionsPending,
  selectIgnoredArticleCodes,
  selectIgnoredArticleRows,
} from "./articleValidationExclusionSelectors";

export function ArticleValidationExclusionsTable({
  diffItems,
}: {
  diffItems: RoomValidationDiffItem[];
}) {
  const projectNo = useAppSelector(selectProjectNo);
  // Remount the dialog on project changes so a pending action cannot cross projects.
  return (
    <ProjectArticleValidationExclusionsTable
      key={projectNo}
      projectNo={projectNo}
      diffItems={diffItems}
    />
  );
}

function ProjectArticleValidationExclusionsTable({
  projectNo,
  diffItems,
}: {
  projectNo: number;
  diffItems: RoomValidationDiffItem[];
}) {
  const dispatch = useAppDispatch();
  const { isError, isFetching, refetch } = openapi.useGetMultiplacerProjectArticleValidationQuery(
    { projectNo },
    { skip: !projectNo }
  );
  const [setIgnored, { isLoading }] =
    openapi.usePostMultiplacerProjectArticleValidationIgnoredMutation();
  const settings = useAppSelector(selectArticleExclusionSettings);
  const codes = useAppSelector(selectIgnoredArticleCodes);
  const ignoredRows = useAppSelector(selectIgnoredArticleRows);
  const pending = useAppSelector(selectArticleExclusionsPending);
  const [selected, setSelected] = useState<{ compCode: string; ignored: boolean } | null>(null);
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const renderAction = useCallback(
    (compCode: string) => {
      const ignored = codes.has(compCode.trim());
      const title = ignored
        ? `Ta med ${compCode} i valideringen igjen`
        : `Unnta ${compCode} fra artikkelvalidering`;
      return (
        <Tooltip
          label={
            settings?.canEdit
              ? title
              : "Kun interne brukere og prosjektadministratorer kan endre unntak"
          }
        >
          <ActionIcon
            aria-label={title}
            variant="subtle"
            color={ignored ? "orange" : "gray"}
            size="sm"
            disabled={!settings?.canEdit || isLoading || isFetching || isError}
            onClick={(event) => {
              event.stopPropagation();
              setError(null);
              setDescription("");
              setSelected({ compCode, ignored: !ignored });
            }}
          >
            {ignored ? <FiRotateCcw size={14} /> : <FiSlash size={14} />}
          </ActionIcon>
        </Tooltip>
      );
    },
    [codes, settings?.canEdit, isLoading, isFetching, isError]
  );

  async function save() {
    if (!selected || (selected.ignored && !description.trim())) return;
    setError(null);
    try {
      await setIgnored({
        body: {
          projectNo,
          ...selected,
          ...(selected.ignored ? { description: description.trim() } : {}),
        },
      }).unwrap();
      // The saved validation stays unchanged; only refresh project configuration.
      await refetch();
      dispatch(openapi.util.invalidateTags(["projects"]));
      setSelected(null);
    } catch {
      setError("Kunne ikke lagre unntaket. Prøv igjen.");
    }
  }

  return (
    <>
      {isError && (
        <Alert color="red" mb="xs">
          Kunne ikke hente prosjektets unntak.{" "}
          <Button variant="subtle" size="xs" onClick={() => void refetch()}>
            Prøv igjen
          </Button>
        </Alert>
      )}
      {pending && (
        <Alert color="blue" mb="xs">
          Unntakslisten er endret. Resultatet nedenfor viser forrige validering. Endringene gjelder
          fra neste synkronisering eller når du velger «Revalider rom».
        </Alert>
      )}
      <ViewerValidationDiffTable
        table="articles"
        occurrenceLayout
        diffItems={diffItems}
        standardCountHeaderName="Krav"
        renderComponentAction={renderAction}
      />
      {ignoredRows.length > 0 && (
        <details style={{ marginTop: 12, flexShrink: 0 }}>
          <summary style={{ cursor: "pointer" }}>
            Unntatt ved siste validering ({ignoredRows.length})
          </summary>
          <Text size="xs" c="dimmed" my="xs">
            Disse radene inngår ikke i treffprosent eller summer. Gjelder valgt liste/ansvar.
          </Text>
          <div style={{ maxHeight: 240, overflow: "auto" }}>
            <table
              style={{ width: "100%", fontSize: 12, textAlign: "left", borderCollapse: "collapse" }}
            >
              <thead>
                <tr>
                  <th>Komponent</th>
                  <th>Krav</th>
                  <th>Modell</th>
                  <th>Differanse</th>
                </tr>
              </thead>
              <tbody>
                {ignoredRows.map((row) => (
                  <tr key={row.compCode}>
                    <td style={{ padding: "8px 0" }}>
                      <Group gap={6}>
                        <strong>{row.compCode}</strong>
                        {renderAction(row.compCode)}
                      </Group>
                      <Text size="xs" c="dimmed">
                        {row.label}
                      </Text>
                    </td>
                    <td>{row.standardCount}</td>
                    <td>{row.instanceCount}</td>
                    <td>{row.delta > 0 ? `+${row.delta}` : row.delta}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
      <Modal
        opened={selected != null}
        onClose={() => {
          if (!isLoading) setSelected(null);
        }}
        title={
          selected?.ignored
            ? "Unnta artikkel fra validering?"
            : "Ta med artikkel i valideringen igjen?"
        }
        centered
      >
        <Stack>
          <Text fw={600}>{selected?.compCode}</Text>
          <Text size="sm">
            {selected?.ignored
              ? "Artikkelkoden unntas fra artikkelvalideringen i alle rom i dette prosjektet, på tvers av lister og ansvar. Antall og avvik for koden vil ikke påvirke treffprosenten eller summer. Unntatte rader kan fortsatt vises."
              : "Artikkelkoden tas med i artikkelvalideringen igjen i alle rom i dette prosjektet, på tvers av lister og ansvar."}
          </Text>
          <Alert color="blue">
            Endringen gjelder først ved neste synkronisering som kjører validering. Du kan også
            velge «Revalider rom» for å oppdatere dette rommet med en gang. Resultatet som vises nå
            endres ikke når du lagrer.
          </Alert>
          <Text size="sm" c="dimmed">
            Ingen artikler slettes fra modellen eller dRofus. Standardromvalideringen påvirkes ikke.
          </Text>
          {selected?.ignored && (
            <Textarea
              label="Beskrivelse"
              required
              autosize
              minRows={3}
              description="Forklar hvorfor artikkelen skal unntas fra validering."
              value={description}
              onChange={(event) => setDescription(event.currentTarget.value)}
            />
          )}
          {error && (
            <Alert color="red" role="alert">
              {error}
            </Alert>
          )}
          <Group justify="flex-end">
            <Button variant="neutral" disabled={isLoading} onClick={() => setSelected(null)}>
              Avbryt
            </Button>
            <Button
              loading={isLoading}
              disabled={selected?.ignored && !description.trim()}
              onClick={() => void save()}
            >
              {selected?.ignored ? "Unnta artikkel" : "Ta med igjen"}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
